// src/features/countries/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  continentOptions,
  currencyOptions,
  economicalZoneOptions,
  emuOecdZoneOptions,
  marketGroupOptions,
  marketTypeOptions,
} from "./filter";

// All enum options now use proper numeric string values
const econZoneOptions = economicalZoneOptions;
const contOptions = continentOptions;
const mGroupOptions = marketGroupOptions;
const mTypeOptions = marketTypeOptions;
// emuOecdZoneOptions already uses numeric strings
// currencyOptions are ISO code strings

// Schema definition
const FormSchema = z.object({
  code: z.string().min(1, "Code is required"),
  en_translation: z.string().nullable().optional(),
  fr_translation: z.string().nullable().optional(),
  economical_zone: z.coerce.number({
    message: "Economical Zone is required",
  }),
  market_group: z.coerce.number().optional().nullable(),
  market_type: z.coerce.number({
    message: "Market Type is required",
  }),
  continent: z.coerce.number({ message: "Continent is required" }),
  currency: z.string().nullable().optional(),
  emu_oecd_zone: z.coerce.number().optional().nullable(),
});

export function CountriesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value) => {
      // Convert numeric enum values to strings for form select fields
      if (
        [
          "economical_zone",
          "market_group",
          "market_type",
          "continent",
          "emu_oecd_zone",
        ].includes(key)
      ) {
        return value !== null && value !== undefined ? String(value) : value;
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Countries {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Code & English Translation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="en_translation"
                    label={t(`${table_name}.columns.en_translation`, {
                      ns: "tables",
                      defaultValue: "English Translation",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2: French Translation & Economical Zone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="fr_translation"
                    label={t(`${table_name}.columns.fr_translation`, {
                      ns: "tables",
                      defaultValue: "French Translation",
                    })}
                    control={form.control}
                  />
                  <SelectField
                    name="economical_zone"
                    label={t(`${table_name}.columns.economical_zone`, {
                      ns: "tables",
                      defaultValue: "Economical Zone",
                    })}
                    options={econZoneOptions}
                    control={form.control}
                  />
                </div>

                {/* Row 3: Market Group & Market Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="market_group"
                    label={t(`${table_name}.columns.market_group`, {
                      ns: "tables",
                      defaultValue: "Market Group",
                    })}
                    options={mGroupOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="market_type"
                    label={t(`${table_name}.columns.market_type`, {
                      ns: "tables",
                      defaultValue: "Market Type",
                    })}
                    options={mTypeOptions}
                    control={form.control}
                  />
                </div>

                {/* Row 4: Continent & Currency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="continent"
                    label={t(`${table_name}.columns.continent`, {
                      ns: "tables",
                      defaultValue: "Continent",
                    })}
                    options={contOptions}
                    control={form.control}
                  />
                  <SelectField
                    name="currency"
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 5: EMU OECD Zone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="emu_oecd_zone"
                    label={t(`${table_name}.columns.emu_oecd_zone`, {
                      ns: "tables",
                      defaultValue: "EMU OECD Zone",
                    })}
                    options={emuOecdZoneOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
